import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { getCustomers, quickAssess, getLibrary, getLibraryStats, getObservationCounts } from '../../api'
import { loadErrorMessage, settledList } from '../../utils/loadState'
import CustomerAvatar from '../../components/CustomerAvatar'
import './ConsultantHome.css'


// ── Extract primary system display from systems array ───────
function getPrimarySystem(systems) {
  if (!systems || !systems.length) return null
  const primary = systems.find(s => s.is_primary) || systems[0]
  return {
    vendorName: primary.vendor_name || primary.vendor_id || '—',
    productName: primary.product_name || primary.product_id || '',
    status: primary.status || 'unknown',
    connectionStatus: primary.connection_status || 'not_connected',
  }
}

const LOAD_LABELS = {
  customers: 'customers', investigations: 'investigations', stats: 'investigation totals',
  observations: 'observation counts', assessments: 'data readiness',
}

const STATE_LABELS = {
  complete: 'Complete', export: 'Exported', running: 'Running',
  plan: 'Planned', clarification: 'Needs Input', error: 'Error', empty: 'New'
}

export default function ConsultantHome() {
  const navigate = useNavigate()
  const [customers, setCustomers] = useState([])
  const [customerMap, setCustomerMap] = useState({})
  const [customerData, setCustomerData] = useState({})
  const [observationCounts, setObservationCounts] = useState({})
  const [libraryItems, setLibraryItems] = useState([])
  const [libraryStats, setLibraryStats] = useState(null)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  // Each source that failed, by name. A failed source is shown as a failure, never as
  // "0 customers" / "0 tables" / "No investigations yet" (item 201).
  const [errors, setErrors] = useState({})

  async function loadAll() {
    setLoading(true)
    setErrors({})
    const failed = {}
    try {
      const [custRes, libRes, statsRes, obsRes] = await Promise.allSettled([
        getCustomers(),
        getLibrary({ sort: 'newest', page_size: 50 }),
        getLibraryStats(),
        getObservationCounts(),
      ])
      const cust = settledList(custRes, 'contexts', 'customers')
      const list = cust.items || []
      if (cust.error) failed.customers = cust.error
      setCustomers(list)

      const cmap = {}
      list.forEach(c => { cmap[c.customer_id] = c })
      setCustomerMap(cmap)

      const lib = settledList(libRes, 'items')
      if (lib.error) failed.investigations = lib.error
      setLibraryItems(lib.items || [])
      if (statsRes.status === 'fulfilled') setLibraryStats(statsRes.value)
      else { setLibraryStats(null); failed.stats = loadErrorMessage(statsRes.reason) }
      if (obsRes.status === 'fulfilled') setObservationCounts(obsRes.value?.counts || {})
      else { setObservationCounts({}); failed.observations = loadErrorMessage(obsRes.reason) }
      setErrors({ ...failed })
      setLoading(false)

      // Per-customer data readiness. A failed assessment is unknown (no entry), not zero.
      const enriched = {}
      await Promise.allSettled(
        list.map(async c => {
          try {
            const assess = await quickAssess(c.customer_id)
            enriched[c.customer_id] = {
              tableCount: assess.table_count || 0,
              hasData: assess.has_data || false,
              rowCount: assess.total_rows || 0,
            }
          } catch {
            failed.assessments = 'Data readiness could not be read for some customers.'
          }
        })
      )
      setCustomerData(enriched)
      setErrors({ ...failed })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadAll() }, [])

  const filtered = customers.filter(c => {
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return (c.company_name || '').toLowerCase().includes(q) || (c.industry || '').toLowerCase().includes(q)
  })

  function getTimeOfDay() {
    const h = new Date().getHours()
    if (h < 12) return 'morning'
    if (h < 17) return 'afternoon'
    return 'evening'
  }

  function fmtDate(iso) {
    if (!iso) return ''
    const d = new Date(iso)
    const now = new Date()
    const diff = now - d
    if (diff < 3600000) return `${Math.max(1, Math.round(diff / 60000))}m ago`
    if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`
    if (diff < 604800000) return `${Math.round(diff / 86400000)}d ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  function customerName(id) {
    return customerMap[id]?.company_name || id
  }

  // ── Derived data ──────────────────────────────────────────
  const totalTables = Object.values(customerData).reduce((sum, d) => sum + (d.tableCount || 0), 0)
  const totalRows = Object.values(customerData).reduce((sum, d) => sum + (d.rowCount || 0), 0)
  const rowsDisplay = totalRows > 1000 ? `${(totalRows / 1000).toFixed(0)}K` : totalRows.toString()

  const recentEfforts = libraryItems.slice(0, 6)

  const needsAttention = libraryItems.filter(e =>
    ['running', 'clarification', 'error'].includes(e.state)
  )

  // Collect critical items from work efforts that have critical_count > 0
  const criticalItems = []
  libraryItems.forEach(e => {
    const count = e.critical_count || 0
    if (count > 0) {
      criticalItems.push({
        customer_id: e.customer_id,
        effort_title: e.title,
        effort_id: e.id,
        critical_count: count,
        state: e.state,
      })
    }
  })

  const stats = libraryStats || {}

  return (
    <div className="consultant-home">
      <TopNav />

      <main className="ch-main">
        {/* GREETING */}
        <div className="ch-greeting">
          <h1>Good {getTimeOfDay()}</h1>
          {errors.customers ? (
            <p>Your customers could not be loaded.</p>
          ) : (
            <p>You have <strong>{customers.length} active customers</strong> with <strong>{errors.assessments ? 'an unknown number of' : totalTables} data tables</strong> and <strong>{errors.assessments ? 'an unknown number of' : rowsDisplay} rows</strong> loaded.</p>
          )}
        </div>

        {Object.entries(errors).map(([what, err]) => (
          <LoadError key={what} what={LOAD_LABELS[what] || what} error={err} onRetry={loadAll} />
        ))}

        {/* ATTENTION BAR — real active items */}
        {needsAttention.length > 0 && (
          <div className="ch-attention-bar">
            <div className="ch-attention-icon">!</div>
            <div className="ch-attention-text">
              <strong>{needsAttention.length} investigation{needsAttention.length !== 1 ? 's' : ''} need{needsAttention.length === 1 ? 's' : ''} attention</strong>
              {needsAttention.slice(0, 2).map((e, i) => (
                <span key={i} className="ch-att-item">
                  {' — '}<strong>{customerName(e.customer_id)}</strong>: {e.title?.slice(0, 40)}{e.title?.length > 40 ? '…' : ''} ({STATE_LABELS[e.state] || e.state})
                </span>
              ))}
            </div>
            <div className="ch-attention-actions">
              <button className="ch-att-btn primary" onClick={() => {
                const first = needsAttention[0]
                if (first) navigate(`/customers/${first.customer_id}/work-efforts/${first.id}`)
              }}>Review →</button>
            </div>
          </div>
        )}

        {/* ═══ CUSTOMERS TABLE ═══ */}
        <div className="ch-section-header">
          <span className="ch-section-title">Your Customers</span>
          <div className="ch-section-controls">
            <input type="text" className="ch-search-input" placeholder="Search customers..." value={search} onChange={e => setSearch(e.target.value)} />
            <button className="ch-btn-new" onClick={() => navigate('/customers/new')}>+ New Customer</button>
          </div>
        </div>

        <table className="ch-customer-table">
          <thead>
            <tr><th>Customer</th><th>System</th><th>Data</th><th>Observations</th><th>Investigations</th><th>Critical</th><th></th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-400)' }}>Loading customers…</td></tr>
            ) : errors.customers ? (
              <tr><td colSpan={7}><LoadError inline what="customers" error={errors.customers} onRetry={loadAll} /></td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-400)' }}>No customers found</td></tr>
            ) : [...filtered].sort((a, b) => {
              const aObs = observationCounts[a.customer_id]?.total || 0
              const bObs = observationCounts[b.customer_id]?.total || 0
              return bObs - aObs
            }).slice(0, 8).map(c => {
              const sys = getPrimarySystem(c.systems)
              const data = customerData[c.customer_id]
              const custEfforts = libraryItems.filter(e => e.customer_id === c.customer_id)
              const custCrits = custEfforts.reduce((sum, e) => sum + (e.critical_count || 0), 0)
              const obs = observationCounts[c.customer_id] || {}
              const obsTotal = obs.total || 0
              const obsCritical = obs.critical || 0

              return (
                <tr key={c.customer_id} onClick={() => navigate(`/customers/${c.customer_id}`)}>
                  <td>
                    <div className="ch-cust-name-cell">
                      <CustomerAvatar customerId={c.customer_id} companyName={c.company_name} website={c.website} logoBase64={c.logo_base64} size="md" />
                      <div>
                        <div className="ch-cust-name">{c.company_name}</div>
                        <div className="ch-cust-detail">{c.industry || '—'} · {c.employee_count?.toLocaleString() || '—'} employees</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    {sys ? (
                      <span className="ch-system-cell">
                        <span className={`ch-health-dot ${sys.connectionStatus === 'connected' ? 'green' : sys.status === 'active' ? 'amber' : 'gray'}`} />
                        {sys.productName || sys.vendorName}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--gray-400)' }}>—</span>
                    )}
                  </td>
                  <td>
                    <span className={`ch-data-readiness ${data?.hasData ? 'has-data' : ''}`}>
                      {data ? `${data.tableCount || 0} tables` : '—'}
                    </span>
                  </td>
                  <td>
                    {errors.observations ? (
                      <span style={{ color: 'var(--gray-400)', fontSize: 12 }} title="Observation counts could not be loaded">?</span>
                    ) : obsTotal > 0 ? (
                      <span className="ch-obs-badge">
                        {obsTotal}
                        {obsCritical > 0 && <span className="ch-obs-crit">{obsCritical}</span>}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>
                    {custEfforts.length > 0 ? (
                      <span className="ch-work-badge active">{custEfforts.length}</span>
                    ) : (
                      <span className="ch-work-badge idle">—</span>
                    )}
                  </td>
                  <td>
                    {custCrits > 0 ? (
                      <span className="ch-crit-badge">{custCrits}</span>
                    ) : (
                      <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td><span className="ch-arrow-cell">→</span></td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {filtered.length > 8 && (
          <div className="ch-view-all">
            <button className="ch-view-all-btn" onClick={() => navigate('/customers')}>View all {customers.length} customers →</button>
          </div>
        )}

        {/* STATS STRIP */}
        <div className="ch-stats-strip">
          <div className="ch-stat-card">
            <div className="ch-stat-value">{errors.customers ? '—' : customers.length}</div>
            <div className="ch-stat-label">Active Customers</div>
            <div className="ch-stat-sub">{Object.values(customerData).filter(d => d.hasData).length} with data loaded</div>
          </div>
          <div className="ch-stat-card">
            <div className="ch-stat-value green">{errors.stats ? '—' : stats.total_efforts || libraryItems.length}</div>
            <div className="ch-stat-label">Investigations</div>
            <div className="ch-stat-sub">{stats.completed || 0} complete · {stats.in_progress || 0} active</div>
          </div>
          <div className="ch-stat-card">
            <div className="ch-stat-value">{errors.stats ? '—' : stats.total_findings || 0}</div>
            <div className="ch-stat-label">Total Findings</div>
            <div className="ch-stat-sub">{stats.total_critical || 0} critical across all</div>
          </div>
          <div className="ch-stat-card">
            <div className="ch-stat-value green">{errors.stats ? '—' : stats.total_exports || 0}</div>
            <div className="ch-stat-label">Exports Generated</div>
            <div className="ch-stat-sub">Consulting-grade deliverables</div>
          </div>
        </div>

        {/* QUICK NAV */}
        <div className="ch-quick-nav">
          <button className="ch-qn-btn" onClick={() => navigate('/threads')}>
            <span className="ch-qn-icon">📋</span>
            <span className="ch-qn-text">Thread Library</span>
            <span className="ch-qn-sub">{stats.total_efforts || 0} investigations saved</span>
          </button>
          <button className="ch-qn-btn" onClick={() => navigate('/exports')}>
            <span className="ch-qn-icon">📊</span>
            <span className="ch-qn-text">Export Center</span>
            <span className="ch-qn-sub">{stats.total_exports || 0} deliverables</span>
          </button>
          <button className="ch-qn-btn" onClick={() => navigate('/customers')}>
            <span className="ch-qn-icon">🏢</span>
            <span className="ch-qn-text">All Customers</span>
            <span className="ch-qn-sub">{customers.length} active</span>
          </button>
        </div>

        {/* THREE COLUMN GRID */}
        <div className="ch-grid">

          {/* Recent Investigations */}
          <div className="ch-card">
            <div className="ch-card-header">
              <span className="ch-card-title">Recent Investigations</span>
              <a className="ch-card-action" onClick={() => navigate('/threads')}>View all →</a>
            </div>
            <div className="ch-card-body">
              {errors.investigations ? (
                <LoadError inline what="investigations" error={errors.investigations} onRetry={loadAll} />
              ) : recentEfforts.length === 0 ? (
                <div className="ch-card-empty">No investigations yet. Start one from a customer page.</div>
              ) : recentEfforts.map((e, i) => (
                <div key={i} className="ch-resume-item" onClick={() => navigate(`/customers/${e.customer_id}/work-efforts/${e.id}`)}>
                  <CustomerAvatar customerId={e.customer_id} companyName={customerName(e.customer_id)} website={customerMap[e.customer_id]?.website} size="sm" />
                  <div className="ch-resume-info">
                    <div className="ch-resume-title">{e.title?.slice(0, 45)}{e.title?.length > 45 ? '…' : ''}</div>
                    <div className="ch-resume-meta">{customerName(e.customer_id)} · {fmtDate(e.updated_at)}</div>
                  </div>
                  <span className={`ch-resume-badge ${e.state === 'complete' || e.state === 'export' ? 'complete' : e.state === 'running' ? 'running' : e.state === 'clarification' ? 'waiting' : 'attention'}`}>
                    {STATE_LABELS[e.state] || e.state}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Critical Findings */}
          <div className="ch-card">
            <div className="ch-card-header">
              <span className="ch-card-title">Critical Findings</span>
              <span style={{ fontSize: 12, color: criticalItems.length > 0 ? 'var(--red)' : 'var(--gray-400)', fontWeight: 600 }}>{criticalItems.reduce((s, c) => s + c.critical_count, 0)} items</span>
            </div>
            <div className="ch-card-body">
              {errors.investigations ? (
                <LoadError inline what="investigations" error={errors.investigations} onRetry={loadAll} />
              ) : criticalItems.length === 0 ? (
                <div className="ch-card-empty">No critical findings.</div>
              ) : criticalItems.slice(0, 5).map((f, i) => (
                <div key={i} className="ch-brain-item" onClick={() => navigate(`/customers/${f.customer_id}/work-efforts/${f.effort_id}`)}>
                  <div className="ch-brain-top">
                    <div className="ch-brain-dot red" />
                    <div className="ch-brain-label">
                      <strong>{customerName(f.customer_id)}</strong>: {f.effort_title?.slice(0, 55)}{f.effort_title?.length > 55 ? '…' : ''}
                    </div>
                  </div>
                  <div className="ch-brain-sub">{f.critical_count} critical finding{f.critical_count !== 1 ? 's' : ''} · {STATE_LABELS[f.state] || f.state}</div>
                </div>
              ))}
              {criticalItems.length > 5 && (
                <div className="ch-card-more" onClick={() => navigate('/threads')}>+ {criticalItems.length - 5} more →</div>
              )}
            </div>
          </div>

          {/* Customer Coverage */}
          <div className="ch-card">
            <div className="ch-card-header">
              <span className="ch-card-title">Customer Coverage</span>
              <span style={{ fontSize: 12, color: 'var(--gray-400)' }}>{customers.length} customers</span>
            </div>
            <div className="ch-card-body">
              {customers.slice(0, 6).map((c, i) => {
                const data = customerData[c.customer_id]
                const custEfforts = libraryItems.filter(e => e.customer_id === c.customer_id)
                const custFindings = custEfforts.reduce((sum, e) => sum + (e.findings_count || 0), 0)
                const custCrits = custEfforts.reduce((sum, e) => sum + (e.critical_count || 0), 0)
                return (
                  <div key={i} className="ch-coverage-item" onClick={() => navigate(`/customers/${c.customer_id}/dashboard`)}>
                    <CustomerAvatar customerId={c.customer_id} companyName={c.company_name} website={c.website} size="sm" />
                    <div className="ch-coverage-info">
                      <div className="ch-coverage-name">{c.company_name}</div>
                      <div className="ch-coverage-meta">
                        {!data ? 'Data status unknown' : data.hasData ? `${data.tableCount} tables` : 'No data'}
                        {custEfforts.length > 0 ? ` · ${custEfforts.length} investigations` : ''}
                        {custFindings > 0 ? ` · ${custFindings} findings` : ''}
                      </div>
                    </div>
                    {custCrits > 0 && <span className="ch-coverage-crit">{custCrits}</span>}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

      </main>
    </div>
  )
}
